import React, { useState, useEffect, useMemo } from "react";
import { Check, Info, AlertTriangle, Settings, Calculator, Calendar, Stamp, FolderDown } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import FiscalSummaryCard from "../components/fiscal/FiscalSummaryCard";
import PensionPlanCard from "../components/fiscal/PensionPlanCard";
import RavvedimentoCalculator from "../components/fiscal/RavvedimentoCalculator";
import RateizzazioneCalculator from "../components/fiscal/RateizzazioneCalculator";
import BolloSection from "../components/fiscal/BolloSection";
import ExportCommercialistaCard from "../components/fiscal/ExportCommercialistaCard";
import HelpTooltip from "../components/common/HelpTooltip";
import { useAppState } from "../hooks/useAppState";
import { ATECO_CODES, INPS_TYPES } from "../constants/ateco";
import { getAliquotaINPS } from "../constants/aliquote";
import {
  getSogliePerAnno,
  calcolaAnniAttivita,
  getAliquotaSostitutiva,
  calcolaImponibile,
  calcolaInpsDovuto,
  risolviVersamentiAnno,
} from "../utils/fiscalEngine";

const currentYear = new Date().getFullYear();

function formatPercent(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "percent",
    minimumFractionDigits: 2,
  }).format(value / 100);
}

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
}

// Converte numero in stringa con virgola italiana
function numberToItalian(num) {
  if (!num) return "";
  const hasDecimals = num % 1 !== 0;
  return hasDecimals
    ? num.toFixed(2).replace(".", ",")
    : String(num);
}

// Converte stringa italiana in numero
function italianToNumber(str) {
  if (!str) return 0;
  let cleaned = str.replace(/\s/g, "");
  if (cleaned.includes(".") && cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(",")) {
    cleaned = cleaned.replace(",", ".");
  }
  return parseFloat(cleaned) || 0;
}

export default function FiscalSettingsPage() {
  const {
    user,
    darkMode,
    toggleDarkMode,
    invoices,
    fiscalSettings,
    setFiscalSettings,
    getFiscalDataForYear,
    setFiscalDataForYear,
    appSettings,
    updateAppSettings,
    pensionPlan,
    updatePensionGoal,
    addPensionContribution,
    updatePensionContribution,
    deletePensionContribution,
    selectedYear,
    setSelectedYear,
    isSynced,
  } = useAppState();

  // Modal calcolatori
  const [showRavvedimentoModal, setShowRavvedimentoModal] = useState(false);
  const [showRateizzazioneModal, setShowRateizzazioneModal] = useState(false);

  // Anno a cui si riferiscono i versamenti dichiarati a mano. È sempre l'anno
  // precedente, NON selectedYear: contributiInpsPrecedenti vive in fiscalSettings
  // ed è un valore unico (non per anno), quindi i due campi affiancati devono
  // parlare dello stesso anno. È anche dove scrivono già OnboardingWizard e
  // CarryOverModal, così le tre schermate restano allineate.
  const annoVersamenti = currentYear - 1;

  // Stato locale per l'input contributi (stringa con virgola)
  const [contributiInput, setContributiInput] = useState(() =>
    numberToItalian(fiscalSettings.contributiInpsPrecedenti)
  );

  // Sincronizza lo stato locale quando fiscalSettings cambia
  useEffect(() => {
    setContributiInput(numberToItalian(fiscalSettings.contributiInpsPrecedenti));
  }, [fiscalSettings.contributiInpsPrecedenti]);

  // Acconti di imposta sostitutiva versati: vivono in fiscalByYear, non in
  // fiscalSettings. Stesso campo scritto da onboarding e CarryOverModal.
  const accontiVersatiSalvati = getFiscalDataForYear(annoVersamenti).accontiVersatiManuali || 0;
  const [accontiInput, setAccontiInput] = useState(() => numberToItalian(accontiVersatiSalvati));

  useEffect(() => {
    setAccontiInput(numberToItalian(accontiVersatiSalvati));
  }, [accontiVersatiSalvati]);

  // Questa pagina mescola due domande diverse, quindi servono due valori.
  //
  // *Oggi*: stato attuale dell'utente — badge "N° anno", card "Regime Fiscale"
  // ("hai diritto al 5% fino al …"), riepilogo configurazione, validazione del
  // form. Sono affermazioni sul presente, non sull'anno selezionato.
  const anniAttivitaOggi = calcolaAnniAttivita(fiscalSettings.pivaYearStart, currentYear);
  const aliquotaOggi = getAliquotaSostitutiva(fiscalSettings.pivaYearStart, currentYear);
  const isPrimoAnnoOggi = anniAttivitaOggi === 1;
  //
  // *Anno selezionato*: calcoli fiscali di quell'anno (FiscalSummaryCard,
  // PensionPlanCard). Il 2025 resta al 5% anche guardandolo nel 2030.
  const anniAttivitaAnno = calcolaAnniAttivita(fiscalSettings.pivaYearStart, selectedYear);
  const aliquotaAnno = getAliquotaSostitutiva(fiscalSettings.pivaYearStart, selectedYear);
  const isPrimoAnnoSelezionato = anniAttivitaAnno === 1;

  const annoFineAgevolazione = fiscalSettings.pivaYearStart + 4;
  // Soglie ragguagliate per l'anno di apertura (mostrate accanto alla data P.IVA).
  const sogliePrimoAnno = getSogliePerAnno(fiscalSettings, fiscalSettings.pivaYearStart);

  // Aliquota INPS dinamica basata su anno e tipo gestione
  const inpsRate = getAliquotaINPS(fiscalSettings.inpsType, selectedYear);

  // Calcola incassi per l'anno selezionato
  const incassiAnno = useMemo(() => {
    const getYear = (dateStr) => dateStr ? new Date(dateStr).getFullYear() : 0;

    return invoices
      .filter((inv) => {
        if (!inv.paid) return false;
        const dateToUse = inv.paymentDate || inv.issueDate;
        return dateToUse && getYear(dateToUse) === selectedYear;
      })
      .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);
  }, [invoices, selectedYear]);

  // Incassi dell'anno dei versamenti (stessa regola di cassa usata altrove),
  // per stimare l'INPS dovuto su quell'anno e valutare la plausibilità.
  const incassiAnnoVersamenti = useMemo(() => {
    const getYear = (dateStr) => (dateStr ? new Date(dateStr).getFullYear() : 0);
    return invoices
      .filter((inv) => {
        if (!inv.paid) return false;
        const dateToUse = inv.paymentDate || inv.issueDate;
        return dateToUse && getYear(dateToUse) === annoVersamenti;
      })
      .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);
  }, [invoices, annoVersamenti]);

  const inpsDovutoAnnoVersamenti = calcolaInpsDovuto(
    calcolaImponibile(incassiAnnoVersamenti, fiscalSettings.profitabilityCoefficient),
    getAliquotaINPS(fiscalSettings.inpsType, annoVersamenti) / 100
  );

  // Avviso di plausibilità: il solo sforamento non basta (i contributi versati
  // in un anno si riferiscono all'imponibile di quello prima, quindi superare il
  // dovuto è spesso legittimo). Il sintomo di "ho messo tutto in un campo solo" è
  // la combinazione: sforamento + nessun acconto imposta + nessun F24 registrato.
  const mostraAvvisoContributi =
    !isPrimoAnnoOggi &&
    inpsDovutoAnnoVersamenti > 0 &&
    (fiscalSettings.contributiInpsPrecedenti || 0) > inpsDovutoAnnoVersamenti &&
    accontiVersatiSalvati === 0 &&
    risolviVersamentiAnno(getFiscalDataForYear(annoVersamenti)).fonte === "manuale";

  // Validazione campi
  const isAtecoValid = fiscalSettings.atecoCode && fiscalSettings.profitabilityCoefficient > 0;
  const isInpsTypeValid = fiscalSettings.inpsType && inpsRate > 0;
  const isContributiValid = isPrimoAnnoOggi || (fiscalSettings.contributiInpsPrecedenti > 0);

  // I contributi vivono in due posti: fiscalSettings (valore unico, usato dalle
  // viste) e fiscalByYear[anno].contributiInpsVersati (usato dal motore). Vanno
  // scritti insieme, altrimenti modificare il campo non cambia i calcoli.
  const handleContributiBlur = () => {
    const numValue = italianToNumber(contributiInput);
    handleChange("contributiInpsPrecedenti", numValue);
    setFiscalDataForYear(annoVersamenti, { contributiInpsVersati: numValue });
    setContributiInput(numValue > 0 ? numberToItalian(numValue) : "");
  };

  const handleAccontiBlur = () => {
    const numValue = italianToNumber(accontiInput);
    setFiscalDataForYear(annoVersamenti, { accontiVersatiManuali: numValue });
    setAccontiInput(numValue > 0 ? numberToItalian(numValue) : "");
  };

  const handleChange = (field, value) => {
    setFiscalSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (!appSettings.hasConfiguredFiscal) {
      updateAppSettings({ hasConfiguredFiscal: true });
    }
  };

  const handleAtecoChange = (code) => {
    const ateco = ATECO_CODES.find((a) => a.code === code);
    if (ateco) {
      setFiscalSettings((prev) => ({
        ...prev,
        atecoCode: code,
        profitabilityCoefficient: ateco.coefficient,
      }));
      if (!appSettings.hasConfiguredFiscal) {
        updateAppSettings({ hasConfiguredFiscal: true });
      }
    }
  };

  const handleInpsTypeChange = (typeId) => {
    const inpsType = INPS_TYPES.find((t) => t.id === typeId);
    if (inpsType) {
      setFiscalSettings((prev) => ({
        ...prev,
        inpsType: typeId,
      }));
      if (!appSettings.hasConfiguredFiscal) {
        updateAppSettings({ hasConfiguredFiscal: true });
      }
    }
  };

  // Anni disponibili per il selettore
  const years = [selectedYear - 2, selectedYear - 1, selectedYear, selectedYear + 1];

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? 'bg-neutral-950 text-white' : 'bg-neutral-50 text-neutral-900'}`}>
      <TopBar
        user={user}
        notificationsCount={0}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-24">
        {/* Header */}
        <div className="mt-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>Fiscale</h1>
              <p className={`mt-1 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Calcoli imposte, scadenze e impostazioni fiscali
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${darkMode ? 'bg-indigo-900 text-forfy-300' : 'bg-forfy-50 text-indigo-800'}`}>
                {anniAttivitaOggi}° anno
              </span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* SEZIONE 1: Riepilogo Fiscale */}
          <FiscalSummaryCard
            selectedYear={selectedYear}
            incassiAnno={incassiAnno}
            profitabilityCoefficient={fiscalSettings.profitabilityCoefficient}
            contributiPrecedenti={isPrimoAnnoSelezionato ? 0 : (fiscalSettings.contributiInpsPrecedenti || 0)}
            aliquota={aliquotaAnno}
            inpsRate={inpsRate}
            anniAttivita={anniAttivitaAnno}
            fiscalData={getFiscalDataForYear(selectedYear)}
            darkMode={darkMode}
          />

          {/* SEZIONE 2: Piano Pensionistico */}
          <PensionPlanCard
            selectedYear={selectedYear}
            pensionPlan={pensionPlan}
            updatePensionGoal={updatePensionGoal}
            addPensionContribution={addPensionContribution}
            updatePensionContribution={updatePensionContribution}
            deletePensionContribution={deletePensionContribution}
            profitabilityCoefficient={fiscalSettings.profitabilityCoefficient}
            taxRate={aliquotaAnno}
            darkMode={darkMode}
          />

          {/* SEZIONE 2.5: Scadenze Bollo */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Stamp className={`w-5 h-5 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Scadenze Bollo
              </h2>
            </div>
            <BolloSection
              invoices={invoices}
              selectedYear={selectedYear}
              darkMode={darkMode}
            />
          </div>

          {/* SEZIONE 3: Strumenti di Pagamento */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Calculator className={`w-5 h-5 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Strumenti di Pagamento
              </h2>
            </div>
            <p className={`text-sm mb-4 ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
              Calcola rate e sanzioni per i tuoi versamenti fiscali.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card Rateizzazione */}
              <button
                onClick={() => setShowRateizzazioneModal(true)}
                className={`flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800/50 hover:border-forfy-500 hover:bg-forfy-900/20'
                    : 'border-neutral-200 bg-neutral-50 hover:border-forfy-500 hover:bg-forfy-50'
                }`}
              >
                <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-forfy-900/50' : 'bg-forfy-100'}`}>
                  <Calendar className={`w-5 h-5 ${darkMode ? 'text-forfy-300' : 'text-forfy-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                    Calcola Rateizzazione
                  </h3>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    Calcola le rate del saldo di giugno con le maggiorazioni mensili.
                  </p>
                </div>
              </button>

              {/* Card Ravvedimento */}
              <button
                onClick={() => setShowRavvedimentoModal(true)}
                className={`flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800/50 hover:border-amber-500 hover:bg-amber-900/20'
                    : 'border-neutral-200 bg-neutral-50 hover:border-amber-500 hover:bg-amber-50'
                }`}
              >
                <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${darkMode ? 'text-amber-300' : 'text-amber-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                    Calcola Ravvedimento
                  </h3>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    Calcola sanzioni e interessi per pagamenti in ritardo.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* SEZIONE 4: Impostazioni Fiscali */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-6">
              <Settings className={`w-5 h-5 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Impostazioni Fiscali
              </h2>
            </div>

            <div className="space-y-6">
              {/* Dati P.IVA */}
              <div className={`rounded-xl border-2 p-5 ${darkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-200 bg-blue-50/50'}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wide mb-4 ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  Dati Partita IVA
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Anno apertura */}
                  <div>
                    <label
                      htmlFor="pivaYearStart"
                      className={`flex items-center gap-1 text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                    >
                      Anno di apertura
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="pivaYearStart"
                      min={1990}
                      max={currentYear}
                      value={fiscalSettings.pivaYearStart}
                      onChange={(e) =>
                        handleChange("pivaYearStart", parseInt(e.target.value, 10) || currentYear)
                      }
                      className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        darkMode
                          ? 'border-neutral-700 bg-neutral-800 text-white'
                          : 'border-neutral-300 bg-white text-neutral-900'
                      }`}
                    />
                    <p className={`mt-2 text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      Sei al <strong>{anniAttivitaOggi}° anno</strong> di attività → Aliquota: <strong>{aliquotaOggi}%</strong>
                    </p>
                  </div>

                  {/* Data apertura P.IVA (per ragguaglio soglie primo anno) */}
                  <div>
                    <label
                      htmlFor="pivaStartDate"
                      className={`flex items-center gap-1 text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                    >
                      Data apertura P.IVA
                    </label>
                    <input
                      type="date"
                      id="pivaStartDate"
                      value={fiscalSettings.pivaStartDate || ""}
                      onChange={(e) => handleChange("pivaStartDate", e.target.value)}
                      className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        darkMode
                          ? 'border-neutral-700 bg-neutral-800 text-white'
                          : 'border-neutral-300 bg-white text-neutral-900'
                      }`}
                    />
                    {sogliePrimoAnno.isRagguagliata ? (
                      <p className={`mt-2 text-sm ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                        Primo anno: {sogliePrimoAnno.giorniAttivita} giorni di attività → soglia
                        ragguagliata a <strong>{formatCurrency(sogliePrimoAnno.soglia)}</strong> (uscita
                        immediata a {formatCurrency(sogliePrimoAnno.sogliaExit)}).
                      </p>
                    ) : (
                      <p className={`mt-2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                        Serve a ragguagliare le soglie nel primo anno. Se vuota si assume il 1° gennaio.
                      </p>
                    )}
                  </div>

                  {/* Codice ATECO */}
                  <div>
                    <label
                      htmlFor="atecoCode"
                      className={`flex items-center gap-1 text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                    >
                      Codice ATECO
                      <span className="text-red-500">*</span>
                      {isAtecoValid && <Check className={`w-4 h-4 ml-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />}
                    </label>
                    <select
                      id="atecoCode"
                      value={fiscalSettings.atecoCode}
                      onChange={(e) => handleAtecoChange(e.target.value)}
                      className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        darkMode
                          ? 'border-neutral-700 bg-neutral-800 text-white'
                          : 'border-neutral-300 bg-white text-neutral-900'
                      }`}
                    >
                      {ATECO_CODES.map((ateco) => (
                        <option key={ateco.code} value={ateco.code}>
                          {ateco.code} - {ateco.label}
                        </option>
                      ))}
                    </select>
                    <p className={`mt-2 text-sm flex items-center gap-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                      Coefficiente redditività: <strong>{Math.round(fiscalSettings.profitabilityCoefficient * 100)}%</strong>
                      <HelpTooltip text="Percentuale applicata al fatturato per determinare il reddito imponibile. Dipende dal codice ATECO." darkMode={darkMode} />
                    </p>
                  </div>
                </div>
              </div>

              {/* Regime fiscale */}
              <div className={`rounded-xl border-2 p-5 ${darkMode ? 'border-emerald-700 bg-emerald-900/20' : 'border-emerald-200 bg-emerald-50/50'}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wide mb-4 ${darkMode ? 'text-emerald-300' : 'text-emerald-900'}`}>
                  Regime Fiscale
                </h3>

                <div className="flex items-center gap-4">
                  <div
                    className={`px-6 py-4 rounded-xl text-center ${
                      aliquotaOggi === 5
                        ? "bg-emerald-500 text-white"
                        : "bg-amber-500 text-white"
                    }`}
                  >
                    <div className="text-3xl font-bold">{aliquotaOggi}%</div>
                    <div className="text-sm opacity-90">
                      {aliquotaOggi === 5 ? "Startup" : "Standard"}
                    </div>
                  </div>

                  <div className="flex-1">
                    {aliquotaOggi === 5 ? (
                      <div className={`flex items-start gap-2 p-3 rounded-lg ${darkMode ? 'bg-emerald-900/40' : 'bg-emerald-100'}`}>
                        <Info className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`} />
                        <p className={`text-sm ${darkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                          Hai diritto all'aliquota agevolata del <strong>5%</strong> fino al{" "}
                          <strong>{annoFineAgevolazione}</strong> (incluso).
                          Mancano <strong>{5 - anniAttivitaOggi}</strong> anni alla scadenza.
                        </p>
                      </div>
                    ) : (
                      <div className={`flex items-start gap-2 p-3 rounded-lg ${darkMode ? 'bg-amber-900/40' : 'bg-amber-100'}`}>
                        <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`} />
                        <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                          Hai superato i 5 anni di attività. L'aliquota applicabile è il <strong>15%</strong>.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contributi INPS */}
              <div className={`rounded-xl border-2 p-5 ${darkMode ? 'border-orange-700 bg-orange-900/20' : 'border-orange-200 bg-orange-50/50'}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wide mb-4 ${darkMode ? 'text-orange-300' : 'text-orange-900'}`}>
                  Contributi INPS
                </h3>

                <div className="space-y-6">
                  {/* Tipo gestione INPS */}
                  <div>
                    <label
                      htmlFor="inpsType"
                      className={`flex items-center gap-1 text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                    >
                      Tipo di gestione
                      <span className="text-red-500">*</span>
                      {isInpsTypeValid && <Check className={`w-4 h-4 ml-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />}
                      <HelpTooltip text="Gestione Separata per professionisti senza cassa. Artigiani/Commercianti se iscritto alla Camera di Commercio." darkMode={darkMode} />
                    </label>
                    <select
                      id="inpsType"
                      value={fiscalSettings.inpsType}
                      onChange={(e) => handleInpsTypeChange(e.target.value)}
                      className={`w-full max-w-md px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        darkMode
                          ? 'border-neutral-700 bg-neutral-800 text-white'
                          : 'border-neutral-300 bg-white text-neutral-900'
                      }`}
                    >
                      {INPS_TYPES.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.label} ({formatPercent(getAliquotaINPS(type.id, selectedYear))})
                        </option>
                      ))}
                    </select>
                    <p className={`mt-2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                      Aliquota contributiva {selectedYear}: <strong>{formatPercent(inpsRate)}</strong>
                    </p>
                  </div>

                  {/* Codice sede INPS */}
                  <div>
                    <label
                      htmlFor="codiceSedeInps"
                      className={`flex items-center gap-1 text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                    >
                      Codice sede INPS
                      <HelpTooltip text="Codice della sede INPS competente, riportato sui modelli F24 nella sezione INPS. Lo trovi sui tuoi F24 precedenti o nel cassetto previdenziale." darkMode={darkMode} />
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      id="codiceSedeInps"
                      value={fiscalSettings.codiceSedeInps || ""}
                      onChange={(e) => handleChange("codiceSedeInps", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                      placeholder="Es: 5400"
                      className={`w-full max-w-xs px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        darkMode
                          ? 'border-neutral-700 bg-neutral-800 text-white'
                          : 'border-neutral-300 bg-white text-neutral-900'
                      }`}
                    />
                    <p className={`mt-2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                      Verrà precompilato nelle righe INPS dei nuovi F24.
                    </p>
                  </div>

                  {/* Versamenti dell'anno precedente: i due campi stanno insieme
                      di proposito — è la loro adiacenza a comunicare che le due
                      parti dell'F24 vanno tenute separate. */}
                  {!isPrimoAnnoOggi && (
                    <div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="contributiInpsPrecedenti"
                            className={`flex items-center gap-1 text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                          >
                            Contributi INPS versati nel {annoVersamenti} (solo sezione INPS)
                            <span className="text-red-500">*</span>
                            {isContributiValid && <Check className={`w-4 h-4 ml-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />}
                          </label>
                          <div className="relative">
                            <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>€</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              id="contributiInpsPrecedenti"
                              value={contributiInput}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9,.]/g, "");
                                setContributiInput(value);
                              }}
                              onBlur={handleContributiBlur}
                              placeholder="0,00"
                              className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                !isContributiValid
                                  ? darkMode ? "border-red-700 bg-red-900/30 text-white" : "border-red-300 bg-red-50 text-neutral-900"
                                  : darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-300 bg-white text-neutral-900"
                              }`}
                            />
                          </div>
                          {fiscalSettings.contributiInpsPrecedenti > 0 && (
                            <p className={`mt-2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                              Importo inserito: <strong>{formatCurrency(fiscalSettings.contributiInpsPrecedenti)}</strong>
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="accontiImpostaVersati"
                            className={`block text-sm font-medium mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}
                          >
                            Acconti imposta sostitutiva versati nel {annoVersamenti} (solo sezione Erario)
                          </label>
                          <div className="relative">
                            <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>€</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              id="accontiImpostaVersati"
                              value={accontiInput}
                              onChange={(e) => setAccontiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
                              onBlur={handleAccontiBlur}
                              placeholder="0,00"
                              className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-300 bg-white text-neutral-900"
                              }`}
                            />
                          </div>
                          {accontiVersatiSalvati > 0 && (
                            <p className={`mt-2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                              Importo inserito: <strong>{formatCurrency(accontiVersatiSalvati)}</strong>
                            </p>
                          )}
                        </div>
                      </div>

                      {!isContributiValid && !isPrimoAnnoOggi && (
                        <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                          <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                          <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                            Contributi anno precedente mancanti: i calcoli fiscali potrebbero non essere accurati.
                          </p>
                        </div>
                      )}

                      {mostraAvvisoContributi && (
                        <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg ${darkMode ? 'bg-amber-900/30 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
                          <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`} />
                          <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                            L'importo dichiarato ({formatCurrency(fiscalSettings.contributiInpsPrecedenti)}) supera
                            i contributi INPS dovuti sull'anno ({formatCurrency(inpsDovutoAnnoVersamenti)}) e non
                            risultano acconti di imposta. Può essere corretto — i contributi versati nel {annoVersamenti} si
                            riferiscono al {annoVersamenti - 1} — ma controlla di non aver incluso anche le righe
                            <strong> Erario</strong> dei tuoi F24: quelle sono imposta sostitutiva, non contributi.
                          </p>
                        </div>
                      )}

                      <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg ${darkMode ? 'bg-orange-900/40' : 'bg-orange-100'}`}>
                        <Info className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-orange-400' : 'text-orange-700'}`} />
                        <p className={`text-sm ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                          Un F24 contiene due parti separate: la <strong>sezione INPS</strong> (contributi) e la{" "}
                          <strong>sezione Erario</strong> (imposta sostitutiva). Vanno nei due campi qui sopra,
                          ciascuna nel suo — non sommate insieme.
                          <br />
                          Se hai un importo unico e non sai come dividerlo, registra gli F24 completi nella
                          sezione <strong>F24</strong>: la divisione la fa l'app.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Messaggio per il primo anno */}
                  {isPrimoAnnoOggi && (
                    <div className={`flex items-start gap-2 p-3 rounded-lg ${darkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
                      <Info className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`} />
                      <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                        Essendo il tuo <strong>primo anno di attività</strong>, non hai contributi INPS
                        dell'anno precedente da dedurre.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Riepilogo configurazione */}
              <div className={`rounded-xl border p-5 ${darkMode ? 'border-neutral-800 bg-neutral-800/50' : 'border-neutral-200 bg-neutral-50'}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wide mb-4 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  Riepilogo Configurazione
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-neutral-900' : 'bg-white'}`}>
                    <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Anno attività</div>
                    <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{anniAttivitaOggi}°</div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-neutral-900' : 'bg-white'}`}>
                    <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Coeff. redditività</div>
                    <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                      {Math.round(fiscalSettings.profitabilityCoefficient * 100)}%
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-neutral-900' : 'bg-white'}`}>
                    <div className={`text-xs flex items-center gap-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                      Aliquota imposta
                      <HelpTooltip text="5% per i primi 5 anni di attività (se requisiti), poi 15%." darkMode={darkMode} />
                    </div>
                    <div className={`text-lg font-semibold ${aliquotaOggi === 5 ? (darkMode ? "text-emerald-400" : "text-emerald-600") : (darkMode ? "text-amber-400" : "text-amber-600")}`}>
                      {aliquotaOggi}%
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-neutral-900' : 'bg-white'}`}>
                    <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Aliquota INPS {selectedYear}</div>
                    <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                      {inpsRate}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SEZIONE 5: Export per Commercialista */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <FolderDown className={`w-5 h-5 ${darkMode ? 'text-forfy-400' : 'text-forfy-600'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Export per Commercialista
              </h2>
              <HelpTooltip text="Scarica un pacchetto ZIP con riepilogo fiscale, elenco fatture, bolli e anagrafica clienti da inviare al commercialista." darkMode={darkMode} />
            </div>
            <ExportCommercialistaCard darkMode={darkMode} />
          </div>
        </div>
      </main>

      {/* Modal Calcolatori */}
      <RavvedimentoCalculator
        isOpen={showRavvedimentoModal}
        onClose={() => setShowRavvedimentoModal(false)}
        darkMode={darkMode}
      />
      <RateizzazioneCalculator
        isOpen={showRateizzazioneModal}
        onClose={() => setShowRateizzazioneModal(false)}
        darkMode={darkMode}
      />
    </div>
  );
}
